import { CommerceService } from "@/services/commerce"

/**
 * Maps over the customer and address information to form address object
 *
 * @param {object} address
 * @param {object} customer
 *
 * @returns {object}
 */
export const mapCustomerAddressInformation = (address, customer) => {
  let isDefaultBilling = false
  let isDefaultShipping = false
  let isCustomerContact = false
  let isBillingAddress = false
  let isShippingAddress = false

  if (customer?.defaultBillingAddressId) {
    isDefaultBilling = address?.id === customer?.defaultBillingAddressId
  }

  if (customer?.defaultShippingAddressId) {
    isDefaultShipping = address?.id === customer?.defaultShippingAddressId
  }

  isBillingAddress = customer?.billingAddressIds?.includes(address?.id)
  isShippingAddress = customer?.shippingAddressIds?.includes(address?.id)
  isCustomerContact = !isBillingAddress && !isShippingAddress

  const customFields = address?.custom?.fields

  let status = address?.custom?.fields.status
  if (status) {
    switch (status) {
      case "X":
        status = "DELETED"
        break
      case "S":
        status = "CLOSED"
        break
      default:
        status = "ACTIVE"
    }
  }

  const customerAddress = {
    ...address,
    customFields: { ...customFields, status: status },
    isDefaultBilling,
    isDefaultShipping,
    isCustomerContact,
    isBillingAddress,
    isShippingAddress,
    customerId: customer?.id,
  }

  return customerAddress
}

/**
 * Takes in an address, status and custom fields information and
 * returns action object for commercetools
 *
 * @param {object} address
 * @param {string} targetStatus
 * @param {object} customFields
 *
 * @returns {object}
 */
export const formCustomerCustomFieldsAction = async (address, targetStatus, customFields) => {
  const commerceApi = CommerceService.api()

  const versionNr = Number(customFields?.versionNr || 0) + 1
  const customType = (await commerceApi.types().withKey({ key: "customerAddressCustomTypeKey" }).get().execute())?.body

  return {
    action: "changeAddress",
    addressId: address.id,
    address: {
      ...address,
      custom: {
        type: { id: customType.id, typeId: "type" },
        fields: {
          status: targetStatus,
          creationBy: customFields?.creationBy,
          creationAt: customFields?.creationAt,
          modifiedBy: customFields?.modifiedBy,
          modifiedAt: new Date(),
          versionNr: String(versionNr),
          benefitRecipient: customFields?.benefitRecipient || false,
          dateOfBirth: customFields?.dateOfBirth || "",
          modificationSource: "Commercetools",
        },
      },
    },
  }
}

/**
 * Takes customer and target address id and finds the address
 *
 * @param {object} customer
 * @param {object} addressId
 *
 * @returns {object}
 */
export const findCustomerTargetAddress = (customer, addressId) => {
  if (customer?.addresses?.length <= 0) return null

  const targetAddress = customer.addresses.find((address) => address?.id === addressId)

  if (!targetAddress) return null

  return mapCustomerAddressInformation(targetAddress, customer)
}
